// PerfilStreaming.js — /perfil en los proyectos con portada streaming
// (Learning). Banner con las portadas, la foto y el nombre, y dos pestañas:
// «Mis datos» con los mismos campos del registro (RegistroStreaming) y
// «Seguridad» para cambiar la contraseña. Guarda por profile.php (PUT): las
// columnas del usuario y el resto en campos_personalizados (se fusionan).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { profileService, misPaginasService, BACKEND_BASE } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import MuroCursos from '../landing/sections/MuroCursos';
import MiSuscripcion from '../suscripcion/MiSuscripcion';
import useMiPlan from '../../hooks/useMiPlan';
import { PAISES_DEF, LISTAS_DEF, Campo, Grilla, Fuerza, Ojo, nivelClave } from '../landing/RegistroStreaming';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const girar = keyframes`to { transform: rotate(360deg); }`;
const aparecer = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;

const Raiz = styled.div`--acento: ${p => p.$acento}; background: ${p => p.$fondo}; color: #d5d9df; font-family: ${FUENTE}; min-height: 100vh;`;
const Cabecera = styled.header`
  position: relative; overflow: hidden; isolation: isolate; padding: clamp(8rem, 12vw, 9.5rem) 4vw clamp(2rem, 4vw, 2.8rem);
  display: flex; align-items: flex-end; gap: clamp(1.2rem, 3vw, 2rem);
  .foto { position: relative; flex: none; width: clamp(96px, 11vw, 132px); aspect-ratio: 1; border-radius: 50%; overflow: hidden; cursor: pointer;
    background: var(--acento); box-shadow: 0 0 0 4px rgba(255,255,255,.12), 0 20px 50px -20px #000; display: grid; place-items: center; border: 0; padding: 0; font-family: inherit; }
  .foto img { width: 100%; height: 100%; object-fit: cover; }
  .foto > b { font-size: clamp(2rem, 4vw, 3rem); font-weight: 800; color: #fff; }
  .foto > span { position: absolute; inset: auto 0 0; padding: .45rem 0 .55rem; background: rgba(0,0,0,.65); color: #fff; font-size: .75rem; font-weight: 700;
    text-align: center; opacity: 0; transition: opacity .2s; }
  .foto:hover > span, .foto:focus-visible > span { opacity: 1; }
  .foto:focus-visible { outline: 2px solid #fff; outline-offset: 4px; }
  .foto .giro { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(0,0,0,.5); }
  .foto .giro i { width: 26px; height: 26px; border-radius: 50%; border: 3px solid #fff; border-right-color: transparent; animation: ${girar} .7s linear infinite; }
  h1 { margin: 0; font-size: clamp(2.2rem, 5.5vw, 4.4rem); font-weight: 800; font-stretch: 78%; line-height: .95; letter-spacing: -.015em; color: #fff; }
  .correo { margin: .5rem 0 0; color: #c3c9d1; font-size: 1rem; overflow-wrap: anywhere; }
  .chips { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .9rem; }
  .chips span { padding: .3rem .8rem; border-radius: 999px; font-size: .82rem; font-weight: 600; color: #e7eaee;
    background: rgba(0,0,0,.4); box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
  @media (max-width: 600px) { flex-direction: column; align-items: flex-start; }
`;
const Cuerpo = styled.div`
  display: grid; grid-template-columns: 240px minmax(0, 760px); gap: clamp(1.5rem, 4vw, 4rem); padding: clamp(1.5rem, 3vw, 2.5rem) 4vw clamp(4rem, 7vw, 6rem);
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;
const Menu = styled.nav`
  position: sticky; top: 7rem; align-self: start; display: grid; gap: .3rem;
  button, a { display: flex; align-items: center; gap: .7rem; width: 100%; height: 46px; padding: 0 1rem; border: 0; border-radius: 8px; background: none;
    color: #9aa1ab; font: inherit; font-size: .95rem; font-weight: 600; text-align: left; text-decoration: none; cursor: pointer; transition: background .15s, color .15s; }
  button:hover, a:hover { background: rgba(255,255,255,.05); color: #fff; }
  button[aria-selected='true'] { background: rgba(255,255,255,.08); color: #fff; box-shadow: inset 3px 0 0 var(--acento); }
  button:focus-visible, a:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  svg { width: 19px; height: 19px; flex: none; }
  hr { border: 0; height: 1px; margin: .5rem 0; background: rgba(255,255,255,.08); }
  @media (max-width: 860px) { position: static; grid-auto-flow: column; grid-auto-columns: max-content; overflow-x: auto; hr, a { display: none; } }
`;
// Institución y plan de formación del alumno (Learning), arriba de «Mis datos».
const MiPlanCaja = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: .6rem 1.4rem; align-items: center; margin: 0 0 1.6rem; padding: 1rem 1.1rem; border-radius: 12px;
  background: linear-gradient(120deg, color-mix(in srgb, var(--acento) 14%, transparent), rgba(255,255,255,.03) 65%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 35%, transparent);
  .t { font-size: .82rem; font-weight: 700; color: #c3c9d1; }
  b { display: block; margin-top: .15rem; font-size: 1.1rem; color: #fff; }
  .barra { margin-top: .6rem; height: 6px; max-width: 320px; border-radius: 3px; background: rgba(255,255,255,.12); overflow: hidden; }
  .barra i { display: block; height: 100%; background: var(--acento); }
  small { display: block; margin-top: .35rem; font-size: .84rem; color: #9aa1ab; }
  a { display: inline-flex; align-items: center; height: 42px; padding: 0 1.1rem; border-radius: 8px; background: #fff; color: #111; font-weight: 700; text-decoration: none; white-space: nowrap; }
  @media (max-width: 560px) { grid-template-columns: 1fr; a { justify-self: start; } }
`;
const Panel = styled.section`
  padding: clamp(1.4rem, 3vw, 2.2rem); border-radius: 18px; background: linear-gradient(160deg, rgba(255,255,255,.06), rgba(255,255,255,.02));
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.09); animation: ${aparecer} .3s ease-out;
  > h2 { margin: 0; font-size: 1.6rem; font-weight: 800; font-stretch: 85%; color: #fff; }
  > p { margin: .4rem 0 1.5rem; color: #9aa1ab; font-size: .92rem; }
  fieldset { border: 0; margin: 0 0 1.4rem; padding: 0; min-width: 0; }
  legend { display: flex; align-items: center; gap: .6rem; width: 100%; padding: 0; margin-bottom: .9rem; font-size: .95rem; font-weight: 700; color: #fff; }
  legend::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,.08); }
  .fijo input { color: #9aa6b8; cursor: not-allowed; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Barra = styled.div`
  position: ${p => (p.$fija ? 'sticky' : 'relative')}; bottom: 1rem; z-index: 5; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;
  margin-top: 1.2rem; padding: .8rem .8rem .8rem 1.1rem; border-radius: 12px; background: rgba(20,20,20,.92); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1), 0 20px 40px -20px #000; font-size: .9rem; color: #9aa1ab;
  .ok { color: #6ee7a8; } .mal { color: #ffb3b3; }
  .botones { display: flex; gap: .5rem; margin-left: auto; }
`;
const Boton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem; height: 44px; padding: 0 1.3rem; border: 0; border-radius: 8px; cursor: pointer;
  font: inherit; font-size: .95rem; font-weight: 700; transition: filter .15s, background .15s;
  ${p => (p.$sec ? 'background: rgba(255,255,255,.08); color: #fff; &:hover:not(:disabled){ background: rgba(255,255,255,.14); }'
    : 'background: var(--acento); color: #fff; box-shadow: 0 10px 24px -12px var(--acento); &:hover:not(:disabled){ filter: brightness(1.08); }')}
  &:disabled { opacity: .5; cursor: default; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
  .giro { width: 15px; height: 15px; border-radius: 50%; border: 2px solid currentColor; border-right-color: transparent; animation: ${girar} .7s linear infinite; }
`;
const Nota = styled.div`
  display: flex; gap: .7rem; align-items: flex-start; padding: .85rem 1rem; border-radius: 10px; background: rgba(61,220,132,.07);
  box-shadow: inset 0 0 0 1px rgba(61,220,132,.2); font-size: .86rem; line-height: 1.45; color: #c3e9d2;
  svg { flex: none; width: 18px; height: 18px; margin-top: 1px; color: #6ee7a8; }
  a { color: #fff; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 3px; }
`;

const I = {
  plan: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 20.5V5.5a2 2 0 0 1 2-2h10.5v14H7a2 2 0 0 0-2 2Z" /><path d="M5 19.5a1.5 1.5 0 0 0 1.5 1.5h11" /><path d="m9 9.5 2 2 4-4" /></svg>,
  suscripcion: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19M6.5 15h4" /></svg>,
  datos: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4.5 20.5c.9-3.6 3.9-6 7.5-6s6.6 2.4 7.5 6" /></svg>,
  seguridad: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4.5" y="10.5" width="15" height="10" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>,
  cert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="9" r="5" /><path d="m9.5 13.3-1.5 7.2 4-2.2 4 2.2-1.5-7.2" /></svg>,
  prog: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7.5 14.5 3.5-4 3 3 5-6" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3 5 6v5c0 4.4 3 8.3 7 9.5 4-1.2 7-5.1 7-9.5V6Z" /><path d="m9 12 2.2 2.2L15.5 10" /></svg>,
};

// Del perfil guardado a los campos del formulario (el nombre completo guarda
// también el apellido: se separa si coincide con el apellido guardado).
const desdePerfil = (u) => {
  const cp = u?.campos_personalizados || {};
  const completo = (u?.nombre || '').trim();
  const ape = (cp.apellido || '').trim();
  const nombre = ape && completo.toLowerCase().endsWith(ape.toLowerCase()) ? completo.slice(0, completo.length - ape.length).trim() : completo;
  const profesion = cp.profesion || '';
  return {
    nombre, apellido: ape, documento: u?.dni || '', pais: u?.pais || '', ciudad: cp.ciudad || '', telefono: u?.telefono || '',
    institucion: cp.institucion || '', profesion, especialidad: profesion === 'Medicina' ? (u?.especialidad || '') : '',
  };
};

const PerfilStreaming = ({ proyecto }) => {
  const { user, updateUser } = useAuth();
  const secs = proyecto?.config_json?.landing_secciones || [];
  const cat = secs.find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = cat.color_acento || '#E50914';
  const fondo = cat.color_fondo || '#141414';

  const [perfil, setPerfil] = useState(null);
  const [f, setF] = useState(null);
  const [original, setOriginal] = useState(null);
  const [errores, setErrores] = useState({});
  const [estado, setEstado] = useState({ tipo: '', texto: '' });
  const [guardando, setGuardando] = useState(false);
  const [pestana, setPestana] = useState(() => (new URLSearchParams(window.location.search).get('pestana') || 'datos'));
  // «Mi suscripción» solo aparece si el proyecto usa suscripciones.
  const [suscripcion, setSuscripcion] = useState(null);
  useEffect(() => { profileService.miSuscripcion().then(r => setSuscripcion(r.data)).catch(() => setSuscripcion({ aplica: false })); }, []);
  const [subiendo, setSubiendo] = useState(false);
  const archivo = useRef(null);

  // Las listas (instituciones, profesiones…) son las mismas del registro.
  const [listasCfg, setListasCfg] = useState(null);
  useEffect(() => {
    const slug = proyecto?.config_json?.auth_config?.registro_pagina || proyecto?.config_json?.auth_config?.login_pagina;
    if (!slug || !proyecto?.id) return;
    misPaginasService.getBySlug(proyecto.id, slug)
      .then(r => {
        const pag = r.data?.pagina || (r.data?.paginas || [])[0];
        const sec = (pag?.secciones || []).find(x => x.tipo === 'acceso_streaming');
        setListasCfg(sec?.config?.listas || null);
      }).catch(() => {});
  }, [proyecto]);
  const listas = useMemo(() => {
    const l = listasCfg || {};
    const de = (k) => (Array.isArray(l[k]) && l[k].filter(Boolean).length ? l[k].filter(Boolean) : LISTAS_DEF[k]);
    return { ciudades: de('ciudades'), instituciones: de('instituciones'), profesiones: de('profesiones'), especialidades: de('especialidades') };
  }, [listasCfg]);

  useEffect(() => {
    profileService.getProfile().then(r => {
      const u = r.data?.user || {};
      setPerfil(u); const datos = desdePerfil(u); setF(datos); setOriginal(datos);
    }).catch(() => setEstado({ tipo: 'mal', texto: 'No pudimos cargar tu perfil. Recarga la página.' }));
  }, []);

  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setEstado({ tipo: '', texto: '' }); if (errores[k]) setErrores(e => ({ ...e, [k]: undefined })); };
  const cambios = f && original && JSON.stringify(f) !== JSON.stringify(original);
  const prefijo = f ? ((PAISES_DEF.find(([p]) => p === f.pais) || [])[1] || perfil?.codigo_pais || '') : '';
  const esColombia = f?.pais === 'Colombia';
  const esMedicina = f?.profesion === 'Medicina';
  const docFijo = !!(perfil?.dni || '').trim();

  const guardar = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.nombre.trim().length < 2) er.nombre = 'Escribe tu nombre.';
    if (f.apellido.trim().length < 2) er.apellido = 'Escribe tu apellido.';
    if (!docFijo && f.documento && !/^[0-9A-Za-z.-]{3,20}$/.test(f.documento.trim())) er.documento = 'Solo números y letras, sin espacios.';
    if (f.telefono && !/^[0-9\s-]{6,15}$/.test(f.telefono.trim())) er.telefono = 'Escribe solo el número, sin el indicativo.';
    if (!f.institucion && !conPlan) er.institucion = 'Elige tu institución (o «Sin institución»).';
    if (esMedicina && !f.especialidad) er.especialidad = 'Elige tu especialidad.';
    setErrores(er);
    if (Object.keys(er).length) { document.querySelector(`[name="${Object.keys(er)[0]}"]`)?.focus(); return; }
    setGuardando(true);
    try {
      const nombre = `${f.nombre.trim()} ${f.apellido.trim()}`;
      const datos = {
        nombre, telefono: f.telefono.trim(), codigo_pais: f.telefono.trim() ? prefijo : '', pais: f.pais,
        especialidad: esMedicina ? f.especialidad : f.profesion,
        campos_personalizados: { apellido: f.apellido.trim(), institucion: f.institucion, profesion: f.profesion, ciudad: esColombia ? f.ciudad : '' },
      };
      if (!docFijo && f.documento.trim()) datos.dni = f.documento.trim();
      await profileService.updateProfile(datos);
      updateUser?.({ nombre });
      setOriginal(f);
      if (datos.dni) setPerfil(p => ({ ...p, dni: datos.dni }));
      setEstado({ tipo: 'ok', texto: 'Cambios guardados.' });
    } catch (err) {
      setEstado({ tipo: 'mal', texto: err.response?.data?.message || 'No pudimos guardar. Inténtalo de nuevo.' });
    }
    setGuardando(false);
  };

  const subirFoto = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setEstado({ tipo: 'mal', texto: 'La foto pesa más de 5 MB. Elige una más liviana.' }); return; }
    setSubiendo(true);
    try {
      const r = await profileService.uploadAvatar(file);
      setPerfil(p => ({ ...p, avatar_url: r.data?.avatar_url }));
      updateUser?.({ avatar_url: r.data?.avatar_url });
      setEstado({ tipo: 'ok', texto: 'Foto actualizada.' });
    } catch (err) {
      setEstado({ tipo: 'mal', texto: err.response?.data?.message || 'No pudimos subir la foto. Usa JPG, PNG o WebP.' });
    }
    setSubiendo(false);
  };

  // ---- Seguridad ----
  const [clave, setClave] = useState({ actual: '', nueva: '', repetir: '' });
  const [ver, setVer] = useState(false);
  const [estadoClave, setEstadoClave] = useState({ tipo: '', texto: '' });
  const [cambiando, setCambiando] = useState(false);
  const nivel = nivelClave(clave.nueva);
  const cambiarClave = async (e) => {
    e.preventDefault();
    if (!clave.actual) { setEstadoClave({ tipo: 'mal', texto: 'Escribe tu contraseña actual.' }); return; }
    if (clave.nueva.length < 6) { setEstadoClave({ tipo: 'mal', texto: 'La nueva contraseña debe tener al menos 6 caracteres.' }); return; }
    if (clave.nueva !== clave.repetir) { setEstadoClave({ tipo: 'mal', texto: 'Las contraseñas nuevas no coinciden.' }); return; }
    setCambiando(true);
    try {
      await profileService.changePassword(clave.actual, clave.nueva);
      setClave({ actual: '', nueva: '', repetir: '' });
      setEstadoClave({ tipo: 'ok', texto: 'Contraseña actualizada.' });
    } catch (err) {
      setEstadoClave({ tipo: 'mal', texto: err.response?.data?.message || 'La contraseña actual no es correcta.' });
    }
    setCambiando(false);
  };

  const mp = useMiPlan({ detalle: true });
  const conPlan = mp.modo === 'plan';
  const nombreMostrar = perfil?.nombre || user?.nombre || user?.name || '';
  const iniciales = nombreMostrar.split(/\s+/).filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase() || '?';
  const foto = perfil?.avatar_url ? `${BACKEND_BASE}/${perfil.avatar_url}` : null;
  const desde = perfil?.created ? new Date(String(perfil.created).replace(' ', 'T')) : null;
  const cp = perfil?.campos_personalizados || {};
  const chips = [conPlan && mp.subrol_label, mp.modo === 'inactivo' && 'Acceso inactivo', original?.profesion && (original.profesion === 'Medicina' && original.especialidad ? original.especialidad : original.profesion), original?.pais, desde && `Desde ${MESES[desde.getMonth()]} de ${desde.getFullYear()}`].filter(Boolean);
  const campo = (k, etiqueta, entrada, { req, ayuda, clase } = {}) => (
    <Campo $error={!!errores[k]} className={clase}>
      <span>{etiqueta}{req && <i aria-hidden="true"> *</i>}</span>
      {entrada}
      {(errores[k] || ayuda) && <small id={`ayuda-${k}`}>{errores[k] || ayuda}</small>}
    </Campo>
  );
  const attrs = (k) => ({ name: k, value: f?.[k] ?? '', onChange: e => set(k, e.target.value), 'aria-invalid': !!errores[k], 'aria-describedby': errores[k] ? `ayuda-${k}` : undefined });

  return (
    <Raiz $acento={acento} $fondo={fondo}>
      <Cabecera>
        <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} opacidad={0.5} />
        <button type="button" className="foto" onClick={() => archivo.current?.click()} aria-label="Cambiar foto de perfil">
          {foto ? <img src={foto} alt="" /> : <b>{iniciales}</b>}
          <span>Cambiar foto</span>
          {subiendo && <div className="giro"><i /></div>}
        </button>
        <input ref={archivo} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={subirFoto} />
        <div>
          <h1>{nombreMostrar || 'Mi perfil'}</h1>
          <p className="correo">{perfil?.email || user?.email}</p>
          {chips.length > 0 && <div className="chips">{chips.map(c => <span key={c}>{c}</span>)}</div>}
        </div>
      </Cabecera>

      <Cuerpo>
        <Menu aria-label="Secciones del perfil" role="tablist">
          <button type="button" role="tab" aria-selected={pestana === 'datos'} onClick={() => setPestana('datos')}>{I.datos}Mis datos</button>
          {suscripcion?.aplica && (
            <button type="button" role="tab" aria-selected={pestana === 'suscripcion'} onClick={() => setPestana('suscripcion')}>{I.suscripcion}Mi suscripción</button>
          )}
          <button type="button" role="tab" aria-selected={pestana === 'seguridad'} onClick={() => setPestana('seguridad')}>{I.seguridad}Seguridad</button>
          <hr />
          {conPlan && <Link to="/mi-plan">{I.plan}Mi plan de formación</Link>}
          <Link to="/contenidos">{I.prog}Mi progreso</Link>
          <Link to="/certificados">{I.cert}Mis certificados</Link>
        </Menu>

        {pestana === 'suscripcion' && suscripcion?.aplica ? (
          <Panel role="tabpanel" aria-label="Mi suscripción">
            <h2>Mi suscripción</h2>
            <p>Tu plan, hasta cuándo tienes acceso y qué cursos incluye.</p>
            <MiSuscripcion datos={suscripcion} tema="oscuro" acento={acento} />
          </Panel>
        ) : pestana === 'datos' || pestana === 'suscripcion' ? (
          <Panel role="tabpanel" aria-label="Mis datos">
            <h2>Mis datos</h2>
            <p>Mantén tus datos al día: con ellos se emiten tus certificados.</p>
            {conPlan && (
              <MiPlanCaja>
                <div>
                  <span className="t">Institución: {mp.subrol_label}</span>
                  <b>{mp.plan?.nombre}</b>
                  {mp.avance && <div className="barra" aria-hidden="true"><i style={{ width: `${mp.avance.pct}%` }} /></div>}
                  {mp.avance && <small>{mp.avance.completo ? 'Completaste tu plan de formación' : `${mp.avance.pct} % del plan · ${mp.avance.cursos_terminados} de ${mp.avance.cursos_total} cursos terminados`}</small>}
                </div>
                <Link to="/mi-plan">Ver mi plan</Link>
              </MiPlanCaja>
            )}
            {mp.modo === 'inactivo' && (
              <MiPlanCaja>
                <div><span className="t">Estado de tu acceso</span><b>Inactivo</b>
                  <small>No tienes cursos disponibles. Renueva tu suscripción o escríbenos para reactivarlo; tu avance y tus certificados siguen guardados.</small></div>
                <Link to="/contactanos?tema=cuenta">Escríbenos</Link>
              </MiPlanCaja>
            )}
            {!f ? <p>Cargando…</p> : (
              <form onSubmit={guardar} noValidate>
                <fieldset>
                  <legend>Datos personales</legend>
                  <Grilla>
                    {campo('nombre', 'Nombre', <div className="caja"><input {...attrs('nombre')} autoComplete="given-name" /></div>, { req: true })}
                    {campo('apellido', 'Apellido', <div className="caja"><input {...attrs('apellido')} autoComplete="family-name" /></div>, { req: true })}
                    {campo('documento', 'Documento de identidad', <div className={`caja${docFijo ? ' fijo' : ''}`}><input {...attrs('documento')} inputMode="numeric" readOnly={docFijo} /></div>,
                      { ayuda: docFijo ? 'No se puede cambiar. Si está mal, escríbenos.' : undefined })}
                    {campo('pais', 'País', (
                      <div className="caja"><select {...attrs('pais')} onChange={e => { set('pais', e.target.value); if (e.target.value !== 'Colombia') set('ciudad', ''); }}>
                        <option value="">Elige un país</option>{PAISES_DEF.map(([p]) => <option key={p} value={p}>{p}</option>)}
                      </select></div>
                    ))}
                    {esColombia && campo('ciudad', 'Ciudad', (
                      <div className="caja"><select {...attrs('ciudad')}><option value="">Elige tu ciudad</option>{listas.ciudades.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                    ))}
                    {campo('telefono', 'Teléfono', (
                      <div className="caja">{prefijo && <span className="prefijo">{prefijo}</span>}<input {...attrs('telefono')} type="tel" inputMode="tel" autoComplete="tel-national" /></div>
                    ), { clase: esColombia ? undefined : undefined })}
                  </Grilla>
                </fieldset>
                <fieldset>
                  <legend>Perfil profesional</legend>
                  <Grilla>
                    {campo('institucion', 'Institución', (
                      conPlan
                        ? <div className="caja fijo"><select value={mp.subrol_label || ''} disabled aria-label="Institución"><option value={mp.subrol_label || ''}>{mp.subrol_label}</option></select></div>
                        : <div className="caja"><select {...attrs('institucion')}><option value="">Elige una</option>
                            {[...new Set([...(f.institucion && !listas.instituciones.includes(f.institucion) ? [f.institucion] : []), ...listas.instituciones])].map(c => <option key={c} value={c}>{c}</option>)}
                          </select></div>
                    ), { req: !conPlan, ayuda: conPlan ? 'La define tu plan de formación. Si no es correcta, escríbenos.' : undefined })}
                    {campo('profesion', 'Profesión', (
                      <div className="caja"><select {...attrs('profesion')} onChange={e => { set('profesion', e.target.value); if (e.target.value !== 'Medicina') set('especialidad', ''); }}>
                        <option value="">Elige una</option>{listas.profesiones.map(c => <option key={c} value={c}>{c}</option>)}
                      </select></div>
                    ))}
                    {esMedicina && campo('especialidad', 'Especialidad', (
                      <div className="caja"><select {...attrs('especialidad')}><option value="">Elige tu especialidad</option>{listas.especialidades.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                    ), { req: true, clase: 'ancho' })}
                  </Grilla>
                </fieldset>
                <fieldset>
                  <legend>Cuenta</legend>
                  <Grilla>
                    {campo('email', 'Correo electrónico', <div className="caja fijo"><input name="email" value={perfil?.email || ''} readOnly /></div>,
                      { clase: 'ancho', ayuda: 'Es tu usuario para entrar; no se puede cambiar desde aquí.' })}
                  </Grilla>
                </fieldset>
                <Nota>
                  {I.check}
                  <span>{cp.acepta_terminos
                    ? <>Aceptaste los <Link to="/terminos-de-uso">términos y condiciones</Link> y la <Link to="/politica-de-privacidad">política de privacidad</Link>{(cp.acepta_terminos.match(/\((\d{4}-\d{2}-\d{2})\)/) || [])[1] ? ` el ${(cp.acepta_terminos.match(/\((\d{4}-\d{2}-\d{2})\)/) || [])[1]}` : ''}.</>
                    : <>Al usar la plataforma aceptas los <Link to="/terminos-de-uso">términos y condiciones</Link> y la <Link to="/politica-de-privacidad">política de privacidad</Link>.</>}</span>
                </Nota>
                <Barra role="status" $fija={cambios}>
                  <span className={estado.tipo}>{estado.texto || (cambios ? 'Tienes cambios sin guardar.' : 'Todo al día.')}</span>
                  <div className="botones">
                    {cambios && <Boton type="button" $sec onClick={() => { setF(original); setErrores({}); }}>Descartar</Boton>}
                    <Boton type="submit" disabled={!cambios || guardando}>{guardando ? <><span className="giro" aria-hidden="true" />Guardando…</> : 'Guardar cambios'}</Boton>
                  </div>
                </Barra>
              </form>
            )}
          </Panel>
        ) : (
          <Panel role="tabpanel" aria-label="Seguridad">
            <h2>Seguridad</h2>
            <p>Cambia tu contraseña. Si no la recuerdas, <Link to="/login?vista=olvido" style={{ color: '#fff' }}>pide un enlace para crear una nueva</Link>.</p>
            <form onSubmit={cambiarClave} noValidate>
              <Grilla>
                {[['actual', 'Contraseña actual', 'current-password'], ['nueva', 'Nueva contraseña', 'new-password'], ['repetir', 'Repite la nueva contraseña', 'new-password']].map(([k, l, ac]) => (
                  <Campo key={k} className="ancho">
                    <span>{l}</span>
                    <div className="caja">
                      <input type={ver ? 'text' : 'password'} autoComplete={ac} value={clave[k]} onChange={e => { setClave(c => ({ ...c, [k]: e.target.value })); setEstadoClave({ tipo: '', texto: '' }); }} />
                      {k === 'actual' && <button type="button" className="ojo" onClick={() => setVer(v => !v)} aria-pressed={ver} aria-label={ver ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}><Ojo tachado={ver} /></button>}
                    </div>
                    {k === 'nueva' && clave.nueva && <Fuerza $nivel={nivel} aria-hidden="true">{[1, 2, 3].map(n => <i key={n} className={nivel >= n ? 'on' : ''} />)}</Fuerza>}
                    {k === 'repetir' && clave.repetir && <small style={{ color: clave.repetir === clave.nueva ? '#6ee7a8' : '#ffb3b3' }}>{clave.repetir === clave.nueva ? 'Coinciden.' : 'No coinciden todavía.'}</small>}
                  </Campo>
                ))}
              </Grilla>
              <Barra role="status">
                <span className={estadoClave.tipo}>{estadoClave.texto || 'Mínimo 6 caracteres.'}</span>
                <div className="botones"><Boton type="submit" disabled={cambiando}>{cambiando ? <><span className="giro" aria-hidden="true" />Cambiando…</> : 'Cambiar contraseña'}</Boton></div>
              </Barra>
            </form>
          </Panel>
        )}
      </Cuerpo>
    </Raiz>
  );
};

export default PerfilStreaming;
